/* eslint-disable react-hooks/exhaustive-deps */
import {
  Grid,
  Box,
  Container,
  TextField,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  FormControl,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RFC_PM } from "@utils/expreciones";
import { useAppDispatch, type RootState } from "@store/store";
import { ProgressBar } from "@components/ProgressBar";
import { setCompanyData, setLoanStatus } from "@store/slices/registerSlice";
import { usePatchCuentaMutation } from "@api/account";
import { usePostValidateNextStageMutation } from "@api/applications";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";

const SCREEN_STATUS = "CSDEM";

const DatosEmpresaPm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [triggerPatchCuenta] = usePatchCuentaMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const companyData = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const formik = useFormik({
    initialValues: {
      rfc: companyData.rfc || "",
      name: companyData.name || "",
    },
    validationSchema: yup.object().shape({
      rfc: yup.string().required("Campo requerido").matches(RFC_PM, "Formato incorrecto, inténtalo de nuevo"),
      name: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);
        if (!companyData.is_correct_ciec) {
          await triggerPatchCuenta({
            accountId: clientDetails.id,
            body: {
              company_rfc: values.rfc,
              company_name: values.name,
            },
          }).unwrap();

          await triggerPostNextStage({
            applicationId: application.id,
            statusCode: nextStatus?.code,
            username: `TOFU - ${accountUser.email}`,
          }).unwrap();

          dispatch(
            setCompanyData({
              id: clientDetails.id,
              rfc: values.rfc,
              name: values.name,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: false,
            }),
          );

          dispatch(setLoanStatus(nextStatus?.code));
        }
        navigate(nextStatus?.url);
      } catch (error) {
        const api_error = error as ApiError;
        if (api_error?.data?.errors) {
          formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
          return;
        }
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const handleUpperChange = (values: string) => {
    formik.setFieldValue("rfc", values.toUpperCase());
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pm?.datos_empresa_pm?.titulo || "Registro de empresa"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar currentStep={currentStep} totalSteps={numberOfSteps} />
          </Container>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3}>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
            >
              Ingresa los datos de la empresa
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <FormControl fullWidth>
                <TextField
                  id="rfc"
                  name="rfc"
                  label="RFC de la empresa"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleUpperChange(e.target.value)}
                  value={formik.values.rfc}
                  error={formik.touched.rfc && Boolean(formik.errors.rfc)}
                  helperText={formik.touched.rfc ? formik.errors.rfc : ""}
                  inputProps={{ maxLength: 12 }}
                  disabled={formik.isSubmitting || companyData.is_correct_ciec}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  id="name"
                  name="name"
                  label="Nombre comercial"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name ? formik.errors.name : ""}
                  inputProps={{ maxLength: 150 }}
                  disabled={formik.isSubmitting}
                />
              </FormControl>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default DatosEmpresaPm;
